import React, { FC, useMemo } from "react";
import clsx from "clsx";
import { useTranslation } from "lib/i18n/use-translation";
import { SessionTile } from "components/ui";
import { useGroupSessionActions } from "../../hooks/use-group-session-actions";
import { PageLoading, PageError } from "../page-container";
import {
  LearnerGroupSession,
  LearnerSessionCardWebPartsFragment,
} from "../../api/types";
import { groupContentByDate } from "../dated-list/dateUtils";
import styles from "./scheduled-list.module.scss";

const SessionTileWithActions = ({
  session,
  listName,
  listPosition,
  showActions,
}: {
  session: LearnerSessionCardWebPartsFragment;
  listName: string;
  listPosition: number;
  showActions?: boolean;
}) => {
  const actions = useGroupSessionActions({ session });

  return (
    <SessionTile
      session={session}
      listName={listName}
      listPosition={listPosition}
      actions={actions}
      showActions={showActions}
    />
  );
};

interface ScheduledListProps {
  items: LearnerGroupSession[];
  listName: string;
  loading?: boolean;
  error?: boolean;
  className?: string;
  showActions?: boolean;
}

const ScheduledList: FC<ScheduledListProps> = ({
  items,
  listName,
  loading = false,
  error = false,
  className,
  showActions,
}) => {
  const { t } = useTranslation(["components"]);
  const dateGroups = useMemo(() => {
    return groupContentByDate(items);
  }, [items]);

  if (loading) {
    return <PageLoading />;
  }

  if (error) {
    return <PageError />;
  }

  return (
    <div className={clsx(styles["scheduled-list"], className)}>
      {items.length === 0 && (
        <div className={styles["no-results"]}>
          <h2>{t("components:contentLibrary.emptyTitle")}</h2>
          <p>{t("components:contentLibrary.emptySubtitle")}</p>
        </div>
      )}

      {items.length > 0 && dateGroups && (
        <div className={styles["date-grouped-content"]}>
          {dateGroups.map((group) => (
            <div key={group.date} className={styles["date-group"]}>
              <h3 className={styles["date-label"]}>{group.dateLabel}</h3>
              <div className={styles.grid}>
                {group.sessions.map((session, index) => {
                  return (
                    <SessionTileWithActions
                      key={session.id}
                      session={session as LearnerSessionCardWebPartsFragment}
                      listName={listName}
                      listPosition={index + 1}
                      showActions={showActions}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScheduledList;
